/**
 * ASTRA Theme - Main JavaScript
 * Core functionality for product forms and quantity inputs
 * Fixed for mobile compatibility
 */

// ========================================
// PUB/SUB SYSTEM
// ========================================
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName].map((callback) => callback(data));
    return Promise.all(promises);
  } else {
    return Promise.resolve();
  }
}

// ========================================
// CONSTANTS
// ========================================
const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// ========================================
// UTILITY FUNCTIONS
// ========================================
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

// Cart Performance Tracking
class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

// ========================================
// QUANTITY INPUT COMPONENT
// Handles +/- quantity buttons and input field
// CRITICAL: Works on both desktop AND mobile via click events
// ========================================
if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });

      if (this.input) {
        this.input.addEventListener('change', this.onInputChange.bind(this));
      }

      // CRITICAL: Add click listeners to ALL buttons (+ and -)
      // This works on both desktop and mobile devices
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback() {
      this.validateQtyRules();
      this.quantityUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.quantityUpdate,
        this.validateQtyRules.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber();
      }
    }

    onInputChange(event) {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;

      // Handle plus button
      if (event.target.name === 'plus') {
        if (
          parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
          this.input.value == 0
        ) {
          this.input.value = this.input.dataset.min;
        } else {
          this.input.stepUp();
        }
      } else {
        // Handle minus button
        this.input.stepDown();
      }

      // Dispatch change event if value changed
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

      // Handle minimum value edge case
      if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
        this.input.value = parseInt(this.input.min);
      }
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);

      // Validate minimum
      if (this.input.min) {
        const buttonMinus = this.querySelector(".quantity__button[name='minus']");
        if (buttonMinus) {
          buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
      }

      // Validate maximum
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const buttonPlus = this.querySelector(".quantity__button[name='plus']");
        if (buttonPlus) {
          buttonPlus.classList.toggle('disabled', value >= max);
        }
      }
    }
  }

  customElements.define('quantity-input', QuantityInput);
}

// ========================================
// PRODUCT FORM COMPONENT
// Handles Add to Cart and Buy Now buttons
// CRITICAL: Works on both desktop AND mobile
// ========================================
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.variantIdInput.disabled = false;

        // CRITICAL: Add submit event listener (works on mobile AND desktop)
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Handle Buy Now / Skip Cart buttons
        this.initializeSkipCartButtons();
      }

      initializeSkipCartButtons() {
        // Handle secondary buttons (Buy Now buttons) with data-skip-cart-button attribute
        const skipCartButtons = this.querySelectorAll('[data-skip-cart-button]');
        skipCartButtons.forEach((button) => {
          button.addEventListener('click', this.handleSkipCartClick.bind(this));
        });
      }

      handleSkipCartClick(evt) {
        evt.preventDefault();

        // Get the variant ID
        const variantId = this.variantIdInput.value;
        if (!variantId) return;

        // Add loading state
        const button = evt.currentTarget;
        button.classList.add('loading');
        button.setAttribute('aria-disabled', true);

        // Create form data
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', this.getQuantity());

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        // Add to cart then redirect to checkout
        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Error occurred
              console.error('Add to cart error:', response);
              button.classList.remove('loading');
              button.removeAttribute('aria-disabled');
              this.handleErrorMessage(response.description);
            } else {
              // Success - redirect to checkout
              window.location = '/checkout';
            }
          })
          .catch((e) => {
            console.error(e);
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
          });
      }

      getQuantity() {
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading-overlay__spinner, .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty'))
                this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            }

            const spinner = this.querySelector('.loading-overlay__spinner, .loading__spinner');
            if (spinner) spinner.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
        }
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}

// ========================================
// VARIANT SELECTS COMPONENT
// Handles size/color/variant selection
// CRITICAL: Updates hidden variant ID input when user selects options
// ========================================
if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();

        // Listen for changes on all option selectors (dropdowns, pills, swatches)
        this.addEventListener('change', this.onVariantChange.bind(this));
      }

      onVariantChange() {
        // Get all selected option values
        this.updateOptions();
        this.updateMasterId();
        this.toggleAddButton(true, '', false);
        this.updatePickupAvailability();
        this.removeErrorMessage();
        this.updateVariantStatuses();

        if (!this.currentVariant) {
          this.toggleAddButton(true, '', true);
          this.setUnavailable();
        } else {
          this.updateMedia();
          this.updateURL();
          this.updateVariantInput();
          this.renderProductInfo();
          this.updateShareUrl();
        }
      }

      updateOptions() {
        // Collect selected values from all option inputs
        this.options = Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map(
          (element) => {
            if (element.tagName === 'SELECT') {
              return element.value;
            } else {
              return element.value;
            }
          }
        );
      }

      updateMasterId() {
        // Find the variant that matches all selected options
        this.currentVariant = this.getVariantData().find((variant) => {
          return !variant.options
            .map((option, index) => {
              return this.options[index] === option;
            })
            .includes(false);
        });
      }

      updateMedia() {
        if (!this.currentVariant) return;
        if (!this.currentVariant.featured_media) return;

        const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-"]`);
        mediaGalleries.forEach((mediaGallery) =>
          mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
        );

        const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
        if (!modalContent) return;
        const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
        modalContent.prepend(newMediaModal);
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateShareUrl() {
        const shareButton = document.getElementById(`Share-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateVariantInput() {
        // CRITICAL: Update the hidden input with name="id"
        const productForms = document.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        );

        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateVariantStatuses() {
        const selectedOptionOneVariants = this.variantData.filter(
          (variant) => this.querySelector(':checked').value === variant.option1
        );
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        inputWrappers.forEach((option, index) => {
          if (index === 0) return;
          const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
          const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
          const availableOptionInputsValue = selectedOptionOneVariants
            .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
            .map((variantOption) => variantOption[`option${index + 1}`]);
          this.setInputAvailability(optionInputs, availableOptionInputsValue);
        });
      }

      setInputAvailability(listOfOptions, listOfAvailableOptions) {
        listOfOptions.forEach((input) => {
          if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
            input.classList.remove('disabled');
            input.removeAttribute('disabled');
          } else {
            input.classList.add('disabled');
            input.setAttribute('disabled', 'disabled');
          }
        });
      }

      updatePickupAvailability() {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;

        if (this.currentVariant && this.currentVariant.available) {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
          pickUpAvailability.removeAttribute('available');
          pickUpAvailability.innerHTML = '';
        }
      }

      removeErrorMessage() {
        const section = this.closest('section');
        if (!section) return;

        const productForm = section.querySelector('product-form');
        if (productForm) productForm.handleErrorMessage();
      }

      renderProductInfo() {
        const requestedVariantId = this.currentVariant.id;
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

        fetch(
          `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
            this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
          }`
        )
          .then((response) => response.text())
          .then((responseText) => {
            if (this.currentVariant.id !== requestedVariantId) return;

            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const destination = document.getElementById(`price-${this.dataset.section}`);
            const source = html.getElementById(
              `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
            );

            const skuSource = html.getElementById(
              `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
            );
            const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
            const inventorySource = html.getElementById(
              `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
            );
            const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

            if (source && destination) destination.innerHTML = source.innerHTML;
            if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
            if (skuSource && skuDestination) {
              skuDestination.innerHTML = skuSource.innerHTML;
              skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
            }

            const price = document.getElementById(`price-${this.dataset.section}`);

            if (price) price.classList.remove('visibility-hidden');

            if (inventoryDestination)
              inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
            this.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              window.variantStrings.soldOut
            );

            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId,
                html,
                variant: this.currentVariant,
              },
            });
          });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = window.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        if (!addButton) return;
        addButtonText.textContent = window.variantStrings.unavailable;
        if (price) price.classList.add('visibility-hidden');
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }
    }
  );
}

// ========================================
// CART DRAWER COMPONENT
// Handles cart drawer open/close and content updates
// CRITICAL: Must exist for Add to Cart to work
// ========================================
if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        // Bind close handlers
        this.addEventListener('keyup', (evt) => {
          if (evt.code.toUpperCase() === 'ESCAPE') this.close();
        });

        // Close on overlay click
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }

        // Set up any close buttons
        this.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
          button.addEventListener('click', this.close.bind(this));
        });
      }

      open() {
        // Add active class to show drawer
        this.classList.add('active');

        // Prevent body scroll
        document.body.classList.add('overflow-hidden');

        // Trap focus in drawer
        const firstFocusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
          setTimeout(() => firstFocusable.focus(), 100);
        }
      }

      close() {
        // Remove active class
        this.classList.remove('active');

        // Re-enable body scroll
        document.body.classList.remove('overflow-hidden');
      }

      renderContents(parsedState) {
        // Update cart sections from AJAX response
        if (parsedState.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (sectionElement && parsedState.sections[section.section]) {
              // Create temporary container
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = parsedState.sections[section.section];

              // Find the corresponding element in the response
              const newContent = tempDiv.querySelector(`#${section.id}`);

              if (newContent && sectionElement) {
                sectionElement.innerHTML = newContent.innerHTML;
              }
            }
          });
        }

        // Update cart count in header if present
        if (parsedState.item_count !== undefined) {
          this.updateCartCount(parsedState.item_count);
        }

        // Remove is-empty class if cart has items
        if (parsedState.item_count > 0) {
          this.classList.remove('is-empty');
        } else {
          this.classList.add('is-empty');
        }

        // Open the drawer to show the added item
        this.open();
      }

      getSectionsToRender() {
        // Return array of sections to update
        return [
          {
            id: 'CartDrawer-CartItems',
            section: 'cart-drawer',
          },
        ];
      }

      setActiveElement(element) {
        this.activeElement = element || document.activeElement;
      }

      updateCartCount(count) {
        // Update all cart count badges
        document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach((element) => {
          element.textContent = count;

          // Toggle visibility
          if (count > 0) {
            element.classList.remove('hidden');
            element.removeAttribute('hidden');
          } else {
            element.classList.add('hidden');
            element.setAttribute('hidden', '');
          }
        });
      }
    }
  );
}

// ========================================
// CART DRAWER ITEMS COMPONENT
// Handles quantity updates within the cart drawer
// ========================================
if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    class CartDrawerItems extends HTMLElement {
      constructor() {
        super();

        // Listen for line item updates
        this.addEventListener('change', (event) => {
          if (event.target.classList.contains('quantity__input')) {
            this.onCartUpdate(event);
          }
        });
      }

      onCartUpdate(event) {
        const input = event.target;
        const index = input.dataset.index;
        const quantity = parseInt(input.value);

        // Update line item via AJAX
        this.updateQuantity(index, quantity);
      }

      updateQuantity(line, quantity) {
        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${window.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body,
        })
          .then((response) => response.json())
          .then((parsedState) => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.renderContents(parsedState);
            }
          })
          .catch((error) => {
            console.error('Cart update error:', error);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer-CartItems',
            section: 'cart-drawer',
          },
        ];
      }
    }
  );
}

// ========================================
// INITIALIZE ON LOAD
// ========================================
console.log('ASTRA Main.js loaded - All functionality enabled');
console.log('Custom Elements Initialized:');
console.log('  ✓ quantity-input - Quantity +/- buttons');
console.log('  ✓ product-form - Add to Cart & Buy Now');
console.log('  ✓ variant-selects - Size/Color selection');
console.log('  ✓ cart-drawer - Cart drawer display');
console.log('  ✓ cart-drawer-items - Cart quantity updates');
